import { useRef } from "react";
import { Play, Pause, Square, Mic, Upload, Disc3, Repeat, X } from "lucide-react";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { KEYS, SCALES, fmtTime, AUDIO_ACCEPT } from "@/lib/music";

const selCls = "rounded-none bg-[#0f0d11] border-[#352d40] h-9 text-sm";

export const Transport = ({ playing, time, duration, project, onPlay, onPause, onStop, onRecord, onFile, onSong, onMeta, uploadRef, songRef, onLoopToggle, onLoopClear }) => {
  const loop = project.loop;
  const hasLoop = loop && loop.end > loop.start;
  const fallback = useRef(null);
  const input = uploadRef || fallback;
  return (
    <div className="h-20 bg-[#161319] border-b border-[#352d40] px-6 flex items-center gap-6 overflow-x-auto vox-scroll">
      <div className="flex items-center gap-1.5">
        {playing ? (
          <button data-testid="transport-pause-btn" onClick={onPause} className="w-11 h-11 flex items-center justify-center bg-[#D99B43] text-black hover:bg-amber-300 transition-colors tube-glow"><Pause size={18} fill="black" /></button>
        ) : (
          <button data-testid="transport-play-btn" onClick={onPlay} disabled={!duration} className="w-11 h-11 flex items-center justify-center bg-[#D99B43] text-black hover:bg-amber-300 transition-colors disabled:opacity-40 tube-glow"><Play size={18} fill="black" /></button>
        )}
        <button data-testid="transport-stop-btn" onClick={onStop} className="w-11 h-11 flex items-center justify-center border border-[#352d40] text-stone-300 hover:border-[#D99B43] transition-colors"><Square size={16} /></button>
        <button
          data-testid="transport-loop-toggle"
          onClick={onLoopToggle}
          disabled={!hasLoop}
          title={hasLoop ? "Toggle loop" : "Drag on the ruler to set a loop"}
          className={`w-11 h-11 flex items-center justify-center border transition-colors disabled:opacity-30 ${hasLoop && loop.enabled ? "bg-[#D99B43]/20 border-[#D99B43] text-amber-300" : "border-[#352d40] text-stone-400 hover:border-[#D99B43]"}`}
        >
          <Repeat size={16} />
        </button>
        {hasLoop && (
          <span className="flex items-center gap-1 pl-1">
            <span className="font-mono-vox text-[11px] text-stone-400 whitespace-nowrap" data-testid="transport-loop-range">{fmtTime(loop.start)}–{fmtTime(loop.end)}</span>
            <button data-testid="transport-loop-clear" onClick={onLoopClear} className="p-1 text-stone-500 hover:text-[#E11D48]" aria-label="Clear loop"><X size={12} /></button>
          </span>
        )}
      </div>
      <div className="font-mono-vox text-lg tracking-widest text-amber-400 font-bold min-w-[170px]" data-testid="transport-timecode-display">
        {fmtTime(time)} <span className="text-stone-600 text-sm">/ {fmtTime(duration)}</span>
      </div>
      <span className="h-10 w-px bg-[#352d40]" />
      <div className="flex items-center gap-2">
        <span className="eyebrow">BPM</span>
        <input data-testid="transport-bpm-input" type="number" min={30} max={240} value={project.bpm} onChange={(e) => onMeta({ bpm: Math.max(30, Math.min(240, +e.target.value || 80)) })} className="w-16 h-9 bg-[#0f0d11] border border-[#352d40] px-2 font-mono-vox text-sm outline-none focus:border-[#D99B43]" />
      </div>
      <div className="flex items-center gap-2">
        <span className="eyebrow">Key</span>
        <Select value={project.key} onValueChange={(v) => onMeta({ key: v })}>
          <SelectTrigger data-testid="transport-key-select" className={`${selCls} w-20`}><SelectValue /></SelectTrigger>
          <SelectContent>{KEYS.map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={project.scale} onValueChange={(v) => onMeta({ scale: v })}>
          <SelectTrigger data-testid="transport-scale-select" className={`${selCls} w-40`}><SelectValue /></SelectTrigger>
          <SelectContent>{SCALES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <span className="flex-1" />
      <button data-testid="transport-record-btn" onClick={onRecord} className="btn-crimson"><Mic size={14} /> Record</button>
      <button data-testid="transport-import-song-btn" onClick={() => songRef.current?.click()} className="btn-gothic"><Disc3 size={14} /> Import song</button>
      <button data-testid="transport-upload-btn" onClick={() => input.current?.click()} className="btn-gothic"><Upload size={14} /> Upload vocal</button>
      <input
        ref={songRef}
        data-testid="transport-song-input"
        type="file"
        accept={AUDIO_ACCEPT}
        multiple
        className="hidden"
        onChange={(e) => {
          if (e.target.files?.length) onSong([...e.target.files]);
          e.target.value = "";
        }}
      />
      <input
        ref={input}
        data-testid="transport-upload-input"
        type="file"
        accept={AUDIO_ACCEPT}
        multiple
        className="hidden"
        onChange={(e) => {
          if (e.target.files?.length) onFile([...e.target.files]);
          e.target.value = "";
        }}
      />
    </div>
  );
};
